// Client-side YouTube transcript fetcher using downsub's signed-URL
// pipeline, called via Capacitor's native HTTP plugin so it inherits
// the device's residential IP and skips the browser's CORS check.
//
// Why this exists:
//   - YouTube bot-walls every direct path from data-center IPs
//     (LOGIN_REQUIRED on watch page, PO Token requirement on
//     innertube, captcha on cobalt)
//   - downsub.com fronts a residential-IP backend that CAN talk to
//     YouTube, but their public API is behind Cloudflare and 503s
//     any Supabase/AWS/GCP IP at the edge
//   - The user's phone has a residential IP. Cloudflare doesn't
//     block it. Capacitor's native HTTP plugin bypasses CORS so
//     downsub's locked-down Access-Control-Allow-Origin doesn't
//     matter.
//   - So: do the AES handshake + signed-URL fetch from the device
//     itself for YouTube URLs. Edge function still handles the
//     other platforms.
//
// Algorithm verbatim from downsub.com's bundled main.js:
//   1. AES-encrypt videoId with their hardcoded passphrase
//   2. URL-safe-base64 the result, first-occurrence-only swaps
//   3. GET https://get-info.downsub.com/<token>
//   4. AES-decrypt the returned subtitle URL → signed YouTube
//      timedtext URL (good ~6 hours)
//   5. Fetch the XML and parse <text start dur> cues into prose

import CryptoJS from "crypto-js";
import { Capacitor, CapacitorHttp } from "@capacitor/core";

const DOWNSUB_SECRET   = "zthxw34cdp6wfyxmpad38v52t3hsz6c5";
const DOWNSUB_INFO_URL = "https://get-info.downsub.com/";

const downsubFormat = {
  stringify(p) {
    const o = { ct: p.ciphertext.toString(CryptoJS.enc.Base64) };
    if (p.iv)   o.iv = p.iv.toString();
    if (p.salt) o.s  = p.salt.toString();
    return JSON.stringify(o);
  },
  parse(s) {
    const o = JSON.parse(s);
    const p = CryptoJS.lib.CipherParams.create({
      ciphertext: CryptoJS.enc.Base64.parse(o.ct),
    });
    if (o.iv) p.iv   = CryptoJS.enc.Hex.parse(o.iv);
    if (o.s)  p.salt = CryptoJS.enc.Hex.parse(o.s);
    return p;
  },
};

// Mirror downsub's first-occurrence-only replace exactly. Their
// server validates against this shape — global replaces produce a
// different token and the API rejects it.
function downsubEncode(value) {
  const enc = CryptoJS.AES.encrypt(
    JSON.stringify(value),
    DOWNSUB_SECRET,
    { format: downsubFormat },
  ).toString();
  let b64 = btoa(enc);
  b64 = b64.replace("+", "-");
  b64 = b64.replace("/", "_");
  b64 = b64.replace("=", "");
  return b64.trim();
}

function downsubDecode(token) {
  let d = token.replace("-", "+").replace("_", "/");
  const mod4 = d.length % 4;
  if (mod4) d += "====".substring(mod4);
  const standard = atob(d);
  const out = CryptoJS.AES.decrypt(
    standard,
    DOWNSUB_SECRET,
    { format: downsubFormat },
  ).toString(CryptoJS.enc.Utf8);
  return JSON.parse(out).trim();
}

export function extractYouTubeVideoId(url) {
  try {
    const u = new URL(url);
    if (/(^|\.)youtu\.be$/i.test(u.hostname)) {
      const id = u.pathname.replace(/^\//, "").split("/")[0];
      return /^[\w-]{11}$/.test(id) ? id : null;
    }
    if (!/(^|\.)youtube\.com$/i.test(u.hostname)) return null;
    const v = u.searchParams.get("v");
    if (v && /^[\w-]{11}$/.test(v)) return v;
    const m = u.pathname.match(/^\/(?:shorts|embed|v)\/([\w-]{11})/);
    if (m) return m[1];
    return null;
  } catch {
    return null;
  }
}

function decodeXmlEntities(s) {
  return s
    .replace(/<[^>]+>/g, "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#39;/g, "'")
    .replace(/&#(\d+);/g, (_, n) => String.fromCodePoint(Number(n)));
}

// Native HTTP via Capacitor — bypasses the WebView's CORS check.
// On native iOS this is the iOS URLSession; on web it falls through
// to the regular fetch (which will hit downsub's locked CORS policy
// and fail — but the caller checks isNativePlatform first).
async function httpGet(url) {
  if (Capacitor.isNativePlatform()) {
    const resp = await CapacitorHttp.get({
      url,
      headers: {
        "User-Agent": "Mozilla/5.0 (Macintosh; Intel Mac OS X 14_0) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.0 Safari/605.1.15",
        "Origin":  "https://downsub.com",
        "Referer": "https://downsub.com/",
        "Accept":  "*/*",
      },
    });
    return {
      ok:     resp.status >= 200 && resp.status < 300,
      status: resp.status,
      text:   typeof resp.data === "string" ? resp.data : JSON.stringify(resp.data),
      json:   typeof resp.data === "string" ? null : resp.data,
    };
  }
  // Web fallback — will fail on CORS for downsub but kept for parity
  // with non-Cloudflare endpoints (e.g. the signed timedtext URL).
  const resp = await fetch(url);
  const text = await resp.text();
  return {
    ok:     resp.ok,
    status: resp.status,
    text,
    json:   null,
  };
}

/**
 * Returns the transcript for a YouTube video id via downsub's API.
 * Only works on Capacitor native — web hits CORS / Cloudflare walls.
 *
 * @param {string} videoId — 11-char YouTube id
 * @returns {Promise<{transcript:string, durationSec:number|null}>}
 * @throws when no captions exist, the API rejects us, etc.
 */
export async function fetchYouTubeTranscriptDirect(videoId) {
  if (!Capacitor.isNativePlatform()) {
    throw new Error("YouTube auto-transcribe is mobile-only (Cloudflare blocks data-center IPs).");
  }
  if (!videoId || !/^[\w-]{11}$/.test(videoId)) {
    throw new Error("Invalid YouTube video id");
  }

  const token = downsubEncode(videoId);

  const infoResp = await httpGet(`${DOWNSUB_INFO_URL}${token}`);
  if (!infoResp.ok) {
    throw new Error(`Downsub get-info returned ${infoResp.status}`);
  }
  let info;
  try {
    info = infoResp.json || JSON.parse(infoResp.text);
  } catch {
    throw new Error("Downsub returned non-JSON");
  }
  if (info?.error) throw new Error(`Downsub: ${info.error}`);

  const subtitles = Array.isArray(info?.subtitles) ? info.subtitles : [];
  const autoTrans = Array.isArray(info?.subtitlesAutoTrans) ? info.subtitlesAutoTrans : [];
  const pick =
    subtitles.find((s) => /^en\b/i.test(s.code || "") && !/auto/i.test(s.code || "")) ||
    subtitles.find((s) => /^en/i.test(s.code || "")) ||
    subtitles[0] ||
    autoTrans.find((s) => /^en/i.test(s.code || "")) ||
    autoTrans[0];
  if (!pick?.url) {
    throw new Error(`No captions on this video (state=${info?.state ?? "?"})`);
  }

  let timedtextUrl;
  try { timedtextUrl = downsubDecode(pick.url); }
  catch (e) { throw new Error(`Decode failed: ${e?.message || String(e)}`); }
  if (!/^https?:\/\//.test(timedtextUrl)) {
    throw new Error("Decoded URL malformed");
  }

  // The signed timedtext URL is NOT IP-gated, so it works from any
  // path — but we keep it on the native HTTP plugin for consistency.
  const xmlResp = await httpGet(timedtextUrl);
  if (!xmlResp.ok) throw new Error(`Timedtext returned ${xmlResp.status}`);

  const cues = [];
  const cueRe = /<text\s+start="([\d.]+)"(?:\s+dur="([\d.]+)")?[^>]*>([\s\S]*?)<\/text>/g;
  let match;
  let lastEnd = 0;
  while ((match = cueRe.exec(xmlResp.text)) !== null) {
    const start = Number(match[1]);
    const dur   = Number(match[2] || "0");
    const text  = decodeXmlEntities(match[3]).trim();
    if (text) cues.push({ start, text });
    if (Number.isFinite(start + dur)) lastEnd = Math.max(lastEnd, start + dur);
  }
  if (cues.length === 0) throw new Error("No caption cues in response");

  const transcript = cues.map((c) => c.text).join(" ").replace(/\s+/g, " ").trim();
  if (transcript.length < 30) throw new Error("Transcript too short");

  return {
    transcript,
    durationSec: lastEnd > 0 ? Math.round(lastEnd) : null,
  };
}
